// The server this console talks to: Luwu (api.luwu.dev), or a company's Luwu
// Enterprise server chosen on the sign-in page. Every page reads the API,
// account service and relays from here. Mirrors apps/mobile/src/lib/serverUrl.ts
// and lib/server.ts; the token in localStorage belongs to the server in use.

export const LUWU_API = "https://api.luwu.dev";
export const LUWU_ACCOUNTS = "https://accounts.luwu.dev";
// Relays reachable from a browser: LA serves WSS on 443 (gateway.luwu.dev).
const LA = "12D3KooWA9fNAAskRQT1aZRs95mjjer3KPtvwV1b4ScAZYdxm87i";
const LUWU_BROWSER_RELAYS = [`/dns4/gateway.luwu.dev/tcp/443/tls/ws/p2p/${LA}`];

/** What a server says about itself (/.well-known/luwu.json). */
export interface Server {
  name: string;
  api: string;
  accounts: string;
  /** Sign-in methods: email_code, password, oidc, google, apple. */
  auth: string[];
  relays: string[];
  /** The identity provider's name for the single sign-on button. */
  oidcName: string;
}

const KEY = "luwu-server";

function load(): Server | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const s = JSON.parse(raw) as Partial<Server>;
    if (typeof s.api !== "string" || s.api === LUWU_API) return null;
    return { name: s.name ?? host(s.api), api: s.api, accounts: s.accounts ?? s.api, auth: s.auth ?? ["email_code"], relays: s.relays ?? [], oidcName: s.oidcName ?? "company sign-in" };
  } catch {
    return null;
  }
}

let current: Server | null = load();

export const host = (u: string) => u.replace(/^https?:\/\//, "");
/** The company server in use, or null on Luwu. */
export const currentServer = () => current;
/** Picks the server to sign in to; null (or Luwu's own address) goes back to Luwu. */
export function chooseServer(s: Server | null) {
  current = s && s.api !== LUWU_API ? s : null;
  try {
    if (current) localStorage.setItem(KEY, JSON.stringify(current));
    else localStorage.removeItem(KEY);
  } catch {
    // private mode: this tab only
  }
}
/** Luwu's control plane, or the company server's. */
export const apiBase = () => current?.api ?? LUWU_API;
/** Where people sign in: accounts.luwu.dev, or the company server's account service. */
export const accountsBase = () => current?.accounts ?? LUWU_ACCOUNTS;
/** On luwu.dev: usage, plans and Luwu's admin exist only there. */
export const onLuwu = () => current === null;
/** The sign-in methods offered here (Google is Luwu's own client; company servers use their SSO). */
export const offers = (method: string) => (current ? current.auth.includes(method) : ["email_code", "google"].includes(method));
/** Relays a browser can dial: the company's WebSocket relays if it lists any, else Luwu's. */
export function browserRelays(): string[] {
  const ws = (current?.relays ?? []).filter((r) => /\/wss?\//.test(`${r}/`));
  return ws.length ? ws : LUWU_BROWSER_RELAYS;
}

const loopback = (h: string) => h === "localhost" || h === "127.0.0.1" || h === "[::1]";

/** "luwu.harborlabs.io/" → "https://luwu.harborlabs.io"; plain http only to this machine. */
export function normalizeServerUrl(input: string): { url: string } | { error: "empty" | "invalid" | "insecure"; suggestion?: string } {
  let s = input.trim();
  if (!s) return { error: "empty" };
  if (!/^[a-z][a-z0-9+.-]*:\/\//i.test(s)) s = `https://${s}`;
  const m = /^([a-z][a-z0-9+.-]*):\/\/([^/?#]*)([^?#]*)(.*)$/i.exec(s);
  if (!m) return { error: "invalid" };
  const scheme = m[1]!.toLowerCase();
  const h = m[2]!.toLowerCase();
  const path = m[3]!.replace(/\/+$/, "");
  if (!h || h.includes("@") || m[4] || !/^(\[[0-9a-f:.]+\]|[a-z0-9.-]+)(:\d{1,5})?$/.test(h) || h.startsWith(".") || h.startsWith("-")) return { error: "invalid" };
  const hostname = h.replace(/:\d+$/, "");
  if (scheme === "http" && !loopback(hostname)) return { error: "insecure", suggestion: `https://${h}${path}` };
  if (scheme !== "https" && scheme !== "http") return { error: "invalid" };
  return { url: `${scheme}://${h}${path}` };
}

/** Checks a well-known body. */
export function parseWellKnown(body: unknown): { server: Server } | { error: "not-luwu" | "bad-address" } {
  if (!body || typeof body !== "object") return { error: "not-luwu" };
  const b = body as Record<string, unknown>;
  const str = (v: unknown) => (typeof v === "string" ? v : "");
  const api = str(b.api).replace(/\/+$/, "");
  if (!api) return { error: "not-luwu" };
  const accounts = (str(b.accounts) || api).replace(/\/+$/, "");
  for (const u of [api, accounts]) {
    const n = normalizeServerUrl(u);
    if (!("url" in n) || !/^https?:\/\//.test(u)) return { error: "bad-address" };
  }
  const list = (v: unknown) => (Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : []);
  const auth = list(b.auth);
  const oidc = (b.oidc && typeof b.oidc === "object" ? (b.oidc as Record<string, unknown>) : {}) as { name?: unknown };
  return {
    server: { name: str(b.name) || host(api), api, accounts, auth: auth.length ? auth : ["email_code"], relays: list(b.relays), oidcName: str(oidc.name) || "company sign-in" },
  };
}

/** Reads a server's /.well-known/luwu.json from what someone typed; errors are ready to show. */
export async function fetchServer(input: string): Promise<Server> {
  const n = normalizeServerUrl(input);
  if ("error" in n) {
    if (n.error === "empty") throw new Error("Enter your company's Luwu address.");
    if (n.error === "insecure") throw new Error(`Company servers are reached over https. Try ${n.suggestion ?? ""}.`);
    throw new Error("That isn't a server address. Use the one your company gave you, like https://luwu.example.com.");
  }
  const h = host(n.url);
  let res: Response;
  try {
    res = await fetch(`${n.url}/.well-known/luwu.json`, { headers: { accept: "application/json" } });
  } catch {
    throw new Error(`Couldn't reach ${h}. Check the address and your connection; the server also has to allow ${location.host} (app_origins in its config).`);
  }
  const body = res.ok ? await res.json().catch(() => null) : null;
  const r = parseWellKnown(body);
  if ("error" in r) {
    if (r.error === "bad-address") throw new Error(`${h} gave an address that isn't https. Ask your administrator to check its settings.`);
    throw new Error(`${h} isn't a Luwu server. Check the address.`);
  }
  return r.server;
}
